import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MediaOutletSelector } from './MediaOutletSelector';
import { CustomOutletInput } from './CustomOutletInput';
import { audienceModeService } from '@/services/audienceModeService';
import { toast } from 'sonner';
import { Loader2, MapPin, Tv, Radio as RadioIcon } from 'lucide-react';

interface AudienceSessionFormProps {
  userId: string;
  userType: 'supabase' | 'internal';
  gameId?: string;
  onSuccess?: () => void;
}

export const AudienceSessionForm: React.FC<AudienceSessionFormProps> = ({
  userId,
  userType,
  gameId,
  onSuccess
}) => {
  const [locationType, setLocationType] = useState<'onsite' | 'watching_in' | 'listening_in'>('onsite');
  const [selectedOutletId, setSelectedOutletId] = useState<string | null>(null);
  const [customOutletName, setCustomOutletName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!gameId) {
      toast.error('Please select a game first');
      return;
    }

    // Validate outlet selection for watching/listening
    if (locationType !== 'onsite' && !selectedOutletId && !customOutletName) {
      toast.error('Please select or enter a media outlet');
      return;
    }

    setLoading(true);
    try {
      const result = await audienceModeService.createSession(
        userId,
        userType,
        gameId,
        locationType,
        selectedOutletId || undefined,
        customOutletName || undefined
      );

      if (result.success) {
        toast.success('Audience session started!');
        onSuccess?.();
      } else {
        toast.error(result.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Error creating audience session:', error);
      toast.error('An error occurred while starting your session');
    } finally {
      setLoading(false);
    }
  };

  const resetOutletSelection = () => {
    setSelectedOutletId(null);
    setCustomOutletName('');
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label className="text-base font-semibold">How are you experiencing the game?</Label>
        <RadioGroup
          value={locationType}
          onValueChange={(value: any) => {
            setLocationType(value);
            resetOutletSelection();
          }}
          className="grid gap-4"
        >
          <div className="flex items-center space-x-2 rounded-lg border border-border p-4 hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="onsite" id="onsite" />
            <Label htmlFor="onsite" className="flex items-center gap-2 cursor-pointer flex-1">
              <MapPin className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">At the Venue</div>
                <div className="text-sm text-muted-foreground">I'm watching in person</div>
              </div>
            </Label>
          </div>

          <div className="flex items-center space-x-2 rounded-lg border border-border p-4 hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="watching_in" id="watching_in" />
            <Label htmlFor="watching_in" className="flex items-center gap-2 cursor-pointer flex-1">
              <Tv className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">Watching on TV</div>
                <div className="text-sm text-muted-foreground">Select your channel</div>
              </div>
            </Label>
          </div>

          <div className="flex items-center space-x-2 rounded-lg border border-border p-4 hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="listening_in" id="listening_in" />
            <Label htmlFor="listening_in" className="flex items-center gap-2 cursor-pointer flex-1">
              <RadioIcon className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">Listening on Radio</div>
                <div className="text-sm text-muted-foreground">Select your station</div>
              </div>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {locationType !== 'onsite' && (
        <div className="space-y-4 pt-4 border-t">
          <Label className="text-base font-semibold">
            Select {locationType === 'watching_in' ? 'TV Channel' : 'Radio Station'}
          </Label>
          
          <MediaOutletSelector
            category={locationType === 'watching_in' ? 'TV' : 'Radio'}
            selectedOutletId={selectedOutletId}
            onSelect={(outletId) => {
              setSelectedOutletId(outletId);
              setCustomOutletName('');
            }}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or
              </span>
            </div>
          </div>

          <CustomOutletInput
            value={customOutletName}
            onChange={(value) => {
              setCustomOutletName(value);
              setSelectedOutletId(null);
            }}
            placeholder={`Enter ${locationType === 'watching_in' ? 'channel' : 'station'} name`}
          />
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={loading || !gameId}
        className="w-full"
        size="lg"
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Start Session
      </Button>
    </div>
  );
};
